// ignoring this for the entire file because dnd requires lots of props to be spread
import styled from '@emotion/styled';
import { Draggable } from '@hello-pangea/dnd';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, PropsWithChildren, ReactNode } from 'react';
import { Fragment, useCallback, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { FieldErrors } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileSkill, SkillProficiencyLevel } from '~/shared/data-contract/skill';
import { Badge } from '~/ui/core/badge/Badge';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from '~/ui/core/context-menu/ContextMenu';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconChatBubble7, IconCopy, IconWarning } from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';

import { useWarningSignalsVisible } from '../../../app/common/hooks/use-warning-signals-visible';
import { useSkillProficiencyLevelName } from '../../../app/common/skills';
import { useActiveLanguage } from '../../providers/ActiveLanguageProvider';
import { useActiveThreadStore } from '../comments/ActiveThreadStoreProvider';
import { commentHighlightRowClassName } from '../comments/comment-highlight';
import { useActivateThread } from '../comments/use-activate-thread';
import { useCommentableState } from '../comments/use-commentable-state';
import type { ProfileForm } from '../types';
import { useCopyTextToClipboard } from '../use-copy-text-to-clipboard';
import { ProficiencyLevelDropdownMenu } from './ProficiencyLevelDropdownMenu';
import { SkillContextMenu } from './SkillContextMenu';
import type { JobProfileSkillWithDiff } from './use-skills-with-compentencies';

const SkillRow = styled.div(({ theme }) => ({
    '&[data-state="open"] [role="button"]': {
        backgroundColor: theme.colors.gray[300],
    },
    '& > *': {
        position: 'relative',
        zIndex: 1,
    },
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    ...theme.typography.body.S.medium,
    flexDirection: 'row',
    gap: theme.spacing.l,
    isolation: 'isolate',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing.s,
    paddingTop: theme.spacing.s,
    position: 'relative',
    width: '100%',
}));

const ProficiencyLevel = styled.div<{ readonly hasError?: boolean }>(({ hasError, theme }) => ({
    alignItems: 'center',
    boxShadow: hasError === true ? `0 0 0 1px ${theme.colors.error[500]}` : 'none',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'center',
    padding: theme.spacing.xs,
}));

const DragWrapper = styled.div<{ readonly isDragEnabled: boolean }>(({ isDragEnabled }) => ({
    '& > *': {
        cursor: isDragEnabled ? 'grab' : 'default',
    },
    '&:focus': {
        outline: 'none',
    },
    cursor: isDragEnabled ? 'grab' : 'default',
    outline: 'none',
    width: '100%',
}));

const Row = styled.div(({ theme }) => ({
    display: 'flex',
    flex: '0 0 auto',
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'flex-end',
    position: 'relative',
}));

const SkillBadgeSlot = styled.div({
    display: 'flex',
    flex: '1 1 auto',
    minWidth: 0,
});

const UnstyledButton = styled.button(({ theme }) => ({
    '&:focus-visible': {
        outline: '1px solid',
        outlineColor: theme.colors.orange[800],
    },
    all: 'unset',
}));

type SkillRowContextMenuProps = PropsWithChildren<{
    readonly dataAttributes?: Record<string, string>;
    readonly highlightClassName?: string;
    readonly onComment?: (anchor: HTMLElement | null) => void;
    readonly onOpenSkillDetails: () => void;
    readonly onSelectExistingComment?: () => void;
    readonly skillId: string;
    readonly skillTitle: string;
}>;

const SkillRowContextMenu: FunctionComponent<SkillRowContextMenuProps> = ({
    children,
    dataAttributes,
    highlightClassName,
    onComment,
    onOpenSkillDetails,
    onSelectExistingComment,
    skillTitle,
}) => {
    const translate = useTranslate();
    const copyTextToClipboard = useCopyTextToClipboard();
    const rowRef = useRef<HTMLDivElement>(null);

    const handleCopy = async (): Promise<void> => {
        await copyTextToClipboard(skillTitle);
    };

    return (
        <ContextMenu>
            <ContextMenuTrigger>
                <SkillRow
                    ref={rowRef}
                    className={clsx('group', highlightClassName)}
                    data-testid="skill-row"
                    onClick={onSelectExistingComment}
                    {...dataAttributes}
                >
                    {children}
                </SkillRow>
            </ContextMenuTrigger>
            <ContextMenuContent className="!min-w-[7rem]" onCloseAutoFocus={(event) => event.preventDefault()}>
                <ContextMenuItem onSelect={onOpenSkillDetails}>
                    <span aria-hidden={true} className="h-xl w-xl flex-none" />
                    {translate('pages.job-architecture-v2.open-skill')}
                </ContextMenuItem>
                {!is.undefined(onComment) && (
                    <ContextMenuItem onSelect={() => onComment(rowRef.current)}>
                        <IconChatBubble7 size={12} />
                        {translate('pages.job-architecture-v2.comments.selection-button.label')}
                    </ContextMenuItem>
                )}
                <ContextMenuItem
                    onSelect={() => {
                        void handleCopy();
                    }}
                >
                    <IconCopy size={12} />
                    {translate('common.copy')}
                </ContextMenuItem>
            </ContextMenuContent>
        </ContextMenu>
    );
};

const CommentableSkillRow: FunctionComponent<
    PropsWithChildren<{
        readonly itemId: string;
        readonly newThreadCreationDisabled: boolean;
        readonly onOpenSkillDetails: () => void;
        readonly skillId: string;
        readonly skillTitle: string;
    }>
> = ({ children, itemId, newThreadCreationDisabled, onOpenSkillDetails, skillId, skillTitle }) => {
    const setPendingTarget = useActiveThreadStore((state) => state.setPendingTarget);
    const setIsCommentsOpen = useActiveThreadStore((state) => state.setIsCommentsOpen);
    const activateThread = useActivateThread(itemId);
    const { state, commentId, savedComment } = useCommentableState({ kind: 'skill', itemId, skillId });

    const handleComment = (anchor: HTMLElement | null): void => {
        setIsCommentsOpen(true);
        if (!is.undefined(savedComment)) {
            activateThread(savedComment.id, savedComment.target);
            return;
        }
        const id = crypto.randomUUID();
        setPendingTarget({
            target: { kind: 'skill', skillId },
            anchorRect: anchor?.getBoundingClientRect() ?? new DOMRect(),
            anchorEl: anchor,
            commentId: id,
            prevDetails: null,
        });
    };

    const handleSelectExistingComment = (): void => {
        if (is.undefined(savedComment)) {
            return;
        }
        setIsCommentsOpen(true);
        activateThread(savedComment.id, savedComment.target);
    };

    const dataAttributes: Record<string, string> = {
        'data-anchor-kind': 'skill',
        'data-skill-id': skillId,
        'data-item-id': itemId,
        ...(is.undefined(commentId) ? {} : { 'data-comment-id': commentId }),
    };

    return (
        <SkillRowContextMenu
            dataAttributes={dataAttributes}
            highlightClassName={commentHighlightRowClassName(state)}
            skillId={skillId}
            skillTitle={skillTitle}
            onComment={newThreadCreationDisabled ? undefined : handleComment}
            onOpenSkillDetails={onOpenSkillDetails}
            onSelectExistingComment={handleSelectExistingComment}
        >
            {children}
        </SkillRowContextMenu>
    );
};

const SkillRowFrame: FunctionComponent<{
    readonly children: ReactNode;
    readonly commentable: boolean;
    readonly itemId: string | undefined;
    readonly newThreadCreationDisabled: boolean;
    readonly onOpenSkillDetails: () => void;
    readonly skillId: string;
    readonly skillTitle: string;
}> = ({ children, commentable, itemId, newThreadCreationDisabled, onOpenSkillDetails, skillId, skillTitle }) => {
    if (commentable && !is.undefined(itemId)) {
        return (
            <CommentableSkillRow
                itemId={itemId}
                newThreadCreationDisabled={newThreadCreationDisabled}
                skillId={skillId}
                skillTitle={skillTitle}
                onOpenSkillDetails={onOpenSkillDetails}
            >
                {children}
            </CommentableSkillRow>
        );
    }

    return (
        <SkillRowContextMenu skillId={skillId} skillTitle={skillTitle} onOpenSkillDetails={onOpenSkillDetails}>
            {children}
        </SkillRowContextMenu>
    );
};

export const SkillChipWithProficiencyLabel: FunctionComponent<{
    readonly commentable: boolean;
    readonly hasValidationError?: boolean;
    readonly isEditMode: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onClick: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly onRemoveSkill: (skillId: string) => void;
    readonly skill: JobProfileSkillWithDiff;
}> = ({
    commentable,
    hasValidationError,
    isEditMode,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onClick,
    onRemoveSkill,
    skill,
}) => {
    const { getProficiencyLevelName } = useSkillProficiencyLevelName();
    const translate = useTranslate();
    const { activeLanguage } = useActiveLanguage();
    const isWarningSignalsVisible = useWarningSignalsVisible();
    const proficiencyTriggerRef = useRef<HTMLButtonElement>(null);
    const [isProficiencyOpen, setIsProficiencyOpen] = useState(false);

    const scale = useMemo(() => {
        return <ProficiencyScale active={true} level={skill.level} scaleCount={5} />;
    }, [skill.level]);

    const handleTriggerProficiencyDropdown = (): void => {
        setIsProficiencyOpen(true);
        if (proficiencyTriggerRef.current !== null) {
            proficiencyTriggerRef.current.click();
        }
    };

    const skillTitle = skill.titles.find((title) => title.language === activeLanguage)?.title ?? skill.title;

    const badge = (
        <SkillBadge
            className="max-w-full"
            skill={{
                title: skillTitle,
                type: skill.skillType,
            }}
            onClick={() => onClick(skill.id, skill.level)}
        />
    );

    return (
        <SkillRowFrame
            commentable={commentable}
            itemId={itemId}
            newThreadCreationDisabled={newThreadCreationDisabled}
            skillId={skill.id}
            skillTitle={skillTitle}
            onOpenSkillDetails={() => onClick(skill.id, skill.level)}
        >
            <SkillBadgeSlot>{badge}</SkillBadgeSlot>
            {isWarningSignalsVisible && skill.isSynced === false && (
                <Tooltip>
                    <Tooltip.Trigger asChild={true}>
                        <IconWarning className="text-warning-500" data-testid="skill-not-synced-warning" size={16} />
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                        <Tooltip.Content side="top">
                            {translate(`pages.job-architecture-v2.skills.not-synced-tooltip`)}
                        </Tooltip.Content>
                    </Tooltip.Portal>
                </Tooltip>
            )}
            {skill.diff !== undefined ? (
                <Badge variant={skill.diff === 'added' ? 'success' : 'error'}>
                    {translate(`components.skills.${skill.diff}`)}
                </Badge>
            ) : null}
            <Row>
                <DropdownMenu open={isProficiencyOpen} onOpenChange={setIsProficiencyOpen}>
                    <DropdownMenuTrigger asChild={true}>
                        <UnstyledButton ref={proficiencyTriggerRef}>
                            <ProficiencyLevel
                                data-hasError={hasValidationError}
                                data-skill-id={skill.id}
                                data-testid="proficiency-level"
                                hasError={hasValidationError}
                                title={getProficiencyLevelName({ level: skill.level, skillType: skill.skillType })}
                            >
                                {scale}
                            </ProficiencyLevel>
                        </UnstyledButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="center"
                        collisionPadding={16}
                        side="right"
                        sideOffset={8}
                        style={{ overflow: 'hidden', zIndex: 2000 }}
                    >
                        <ProficiencyLevelDropdownMenu
                            activeLevel={skill.level}
                            disabled={isEditMode === false}
                            id={skill.id}
                            onChangeProficiencyLevel={onChangeProficiencyLevel}
                        />
                    </DropdownMenuContent>
                </DropdownMenu>
                {isEditMode && (
                    <SkillContextMenu
                        onDelete={() => onRemoveSkill(skill.id)}
                        onShowSkillDetails={() => onClick(skill.id, skill.level)}
                        onTriggerProficiencyDropdown={handleTriggerProficiencyDropdown}
                    />
                )}
            </Row>
        </SkillRowFrame>
    );
};

export const SkillList: FunctionComponent<{
    readonly commentable: boolean;
    readonly formErrors?: FieldErrors<ProfileForm>;
    readonly isInEditMode: boolean;
    readonly itemId?: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onChangeProficiencyLevel: (skillId: string, level: JobProfileSkill['level']) => void;
    readonly onRemoveSkill: (skillId: string) => void;
    readonly onSkillChange?: () => void;
    readonly openSkillDetails: (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => void;
    readonly skills: readonly JobProfileSkill[];
}> = ({
    commentable,
    formErrors,
    isInEditMode,
    itemId,
    newThreadCreationDisabled = false,
    onChangeProficiencyLevel,
    onRemoveSkill,
    onSkillChange,
    openSkillDetails,
    skills,
}) => {
    const hasValidationError = formErrors?.competencies !== undefined;

    const handleChangeProficiencyLevel = useCallback(
        (skillId: string, level: JobProfileSkill['level']): void => {
            onSkillChange?.();
            onChangeProficiencyLevel(skillId, level);
        },
        [onSkillChange, onChangeProficiencyLevel],
    );

    const handleRemoveSkill = useCallback(
        (skillId: string): void => {
            onSkillChange?.();
            onRemoveSkill(skillId);
        },
        [onSkillChange, onRemoveSkill],
    );

    return (
        <Fragment>
            {skills.map((skill, skillIndex) => {
                const skillHasError = hasValidationError && typeof skill.level !== 'number';

                return (
                    <Draggable
                        key={skill.id}
                        draggableId={skill.id}
                        index={skillIndex}
                        isDragDisabled={isInEditMode === false || skill.skillType === 'language'}
                    >
                        {(dragProvided, snapshot) => {
                            if (snapshot.isDragging) {
                                return ReactDOM.createPortal(
                                    <DragWrapper
                                        ref={dragProvided.innerRef}
                                        isDragEnabled={isInEditMode}
                                        {...dragProvided.draggableProps}
                                        {...dragProvided.dragHandleProps}
                                    >
                                        <SkillChipWithProficiencyLabel
                                            key={skill.id}
                                            commentable={commentable}
                                            hasValidationError={skillHasError}
                                            isEditMode={isInEditMode}
                                            itemId={itemId}
                                            newThreadCreationDisabled={newThreadCreationDisabled}
                                            skill={skill}
                                            onChangeProficiencyLevel={handleChangeProficiencyLevel}
                                            onClick={openSkillDetails}
                                            onRemoveSkill={handleRemoveSkill}
                                        />
                                    </DragWrapper>,
                                    document.body,
                                );
                            }

                            return (
                                <DragWrapper
                                    ref={dragProvided.innerRef}
                                    isDragEnabled={isInEditMode}
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                >
                                    <SkillChipWithProficiencyLabel
                                        key={skill.id}
                                        commentable={commentable}
                                        hasValidationError={skillHasError}
                                        isEditMode={isInEditMode}
                                        itemId={itemId}
                                        newThreadCreationDisabled={newThreadCreationDisabled}
                                        skill={skill}
                                        onChangeProficiencyLevel={handleChangeProficiencyLevel}
                                        onClick={openSkillDetails}
                                        onRemoveSkill={handleRemoveSkill}
                                    />
                                </DragWrapper>
                            );
                        }}
                    </Draggable>
                );
            })}
        </Fragment>
    );
};
